import { storage } from './storage';

/**
 * The usage stream.
 *
 * Inert until {@link initTelemetry} arms it, so `src/ui` and `src/app.ts` can
 * call {@link track} freely and a test or a dev build sends nothing.
 *
 * Three rules this module exists to keep (GDD §12):
 *
 * - **It is never on the path of play.** Every send is fire-and-forget, no
 *   caller awaits one, and a failure parks the batch rather than surfacing.
 * - **No wall-clock time leaves the device.** Events carry how far into the
 *   session they happened; the server owns the epoch.
 * - **Off means off.** The switch flushes the flip that describes itself, then
 *   purges what is held and stops.
 */

/** Events buffered before the queue flushes on its own. */
const MAX_BATCH = 40;
/** How long a partial batch waits for company. */
const FLUSH_MS = 15_000;
/** Events kept across a failed send. Beyond this the oldest are dropped. */
const MAX_QUEUE = 120;
/** A parked batch older than this is not worth sending. */
const QUEUE_TTL_MS = 48 * 3600 * 1000;
/** `sendBeacon` gives up well before this; staying under it keeps sends whole. */
const MAX_BODY = 24 * 1024;

const QUEUE_KEY = 'wirespan:queue';
const PAYLOAD_VERSION = 1;

/** Why a batch was sent. The server records it; nothing branches on it. */
export type Flush = 'manual' | 'full' | 'idle' | 'hidden' | 'pagehide' | 'freeze' | 'optout';

type Fields = Record<string, string | number | boolean | undefined>;
interface QueuedEvent extends Fields {
  n: string;
  t: number;
}
type Envelope = Record<string, string | number | boolean>;
interface ParkedBatch {
  head: Envelope;
  e: QueuedEvent[];
  at: number;
}

export interface TelemetryOptions {
  /** Where batches go. Same origin, so a content blocker's answer is a choice. */
  url: string;
  enabled: boolean;
  /** The identifying and technical fields every row carries. */
  context: Envelope;
  /** Milliseconds this session has been active. Monotonic, and it pauses. */
  clock: () => number;
  /** Injected by tests. Returns false when the batch could not be handed off. */
  send?: (url: string, body: string) => boolean;
  /** Injected by tests, because a parked batch's age is the one absolute time. */
  now?: () => number;
}

let options: TelemetryOptions | null = null;
let enabled = false;
let buffer: QueuedEvent[] = [];
let seq = 0;
let sessionId = '';
let timer: ReturnType<typeof setTimeout> | null = null;

/**
 * One document load is one session.
 *
 * Held in memory only and gone when the tab closes: it is what lets a session
 * be counted and its events ordered, and it deliberately cannot outlive the
 * page to become a second identifier.
 */
function mintSessionId(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
}

/**
 * The default transport.
 *
 * `text/plain` is deliberate: it keeps the POST a CORS simple request, so a
 * preflight never rides in front of a beacon. The server parses JSON either
 * way. `keepalive` is the fallback for a browser without `sendBeacon`, and for
 * the one case where it refuses (a body over its own limit).
 */
function defaultSend(url: string, body: string): boolean {
  const type = 'text/plain;charset=UTF-8';
  try {
    if (navigator.sendBeacon?.(url, new Blob([body], { type }))) return true;
  } catch {
    /* fall through to fetch */
  }
  try {
    void fetch(url, {
      method: 'POST',
      body,
      keepalive: true,
      headers: { 'content-type': type },
    }).catch(() => undefined);
    return true;
  } catch {
    return false;
  }
}

const nowMs = (): number => options?.now?.() ?? Date.now();

function envelope(flush: Flush, extra: Envelope = {}): Envelope {
  return {
    payloadVersion: PAYLOAD_VERSION,
    sessionId,
    batchSeq: seq++,
    sentAtMs: Math.max(0, Math.round(options?.clock() ?? 0)),
    flush,
    ...(options?.context ?? {}),
    ...extra,
  };
}

/** Batches that failed to send, oldest first. A corrupt blob reads as none. */
async function parked(): Promise<ParkedBatch[]> {
  try {
    const raw = await storage.get(QUEUE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? (parsed as ParkedBatch[]) : [];
  } catch {
    return [];
  }
}

/**
 * Hold a batch that could not be sent, **with the envelope it was built
 * under**. Rebuilding it later would file the events under the wrong session
 * and the wrong build.
 */
async function park(head: Envelope, events: QueuedEvent[]): Promise<void> {
  const held = await parked();
  held.push({ head, e: events, at: nowMs() });
  let total = held.reduce((n, b) => n + b.e.length, 0);
  while (held.length > 1 && total > MAX_QUEUE) total -= held.shift()!.e.length;
  await storage.set(QUEUE_KEY, JSON.stringify(held));
}

async function clearParked(): Promise<void> {
  await storage.set(QUEUE_KEY, '[]');
}

/** Post whatever is buffered. Never throws, never awaited by a caller. */
function post(flush: Flush): void {
  if (!options || !enabled || !buffer.length) return;
  const events = buffer;
  buffer = [];
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }

  const head = envelope(flush);
  const body = JSON.stringify({ v: PAYLOAD_VERSION, batches: [{ ...head, e: events }] });
  const sent = body.length <= MAX_BODY && (options.send ?? defaultSend)(options.url, body);
  if (!sent) void park(head, events);
}

/**
 * Send what an earlier session could not.
 *
 * The wait becomes `queuedMs`, so the server can still place each event at the
 * moment it happened rather than at the moment it finally arrived.
 */
async function drain(): Promise<void> {
  if (!options || !enabled) return;
  const held = await parked();
  if (!held.length) return;
  await clearParked();

  const now = nowMs();
  const fresh = held.filter((b) => now - b.at < QUEUE_TTL_MS);
  if (!fresh.length) return;

  const batches = fresh.map((b) => ({ ...b.head, queuedMs: Math.max(0, now - b.at), e: b.e }));
  const body = JSON.stringify({ v: PAYLOAD_VERSION, batches });
  const sent = body.length <= MAX_BODY && (options.send ?? defaultSend)(options.url, body);
  // A failed drain parks once more rather than looping: the TTL retires it.
  if (!sent) await storage.set(QUEUE_KEY, JSON.stringify(fresh));
}

/** Record something that happened. Cheap, synchronous, and never throws. */
export function track(name: string, fields: Fields = {}): void {
  if (!options || !enabled) return;
  const clean: Fields = {};
  for (const [k, v] of Object.entries(fields)) if (v !== undefined) clean[k] = v;
  buffer.push({ n: name, t: Math.max(0, Math.round(options.clock())), ...clean });

  if (buffer.length >= MAX_BATCH) {
    post('full');
    return;
  }
  // One timeout, never an interval: an interval keeps a test runner alive.
  if (!timer) timer = setTimeout(() => post('idle'), FLUSH_MS);
}

/**
 * Milliseconds this session has been active, or 0 while nothing is armed.
 *
 * The one clock the game measures against, so a duration recorded here and a
 * duration recorded in the transport cannot disagree.
 */
export function sessionMs(): number {
  return Math.max(0, Math.round(options?.clock() ?? 0));
}

/** Send what is buffered now. */
export function flushTelemetry(reason: Flush = 'manual'): void {
  post(reason);
}

/**
 * Throw the switch.
 *
 * Turning it off sends the flip that describes it — the last thing that ever
 * leaves — then stops and purges what was held. Turning it on tries the
 * backlog, because a session that ran while off has nothing to say.
 */
export function setTelemetryEnabled(on: boolean): void {
  if (!options || on === enabled) return;
  if (!on) {
    post('optout');
    enabled = false;
    buffer = [];
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    void clearParked();
    return;
  }
  enabled = true;
  void drain();
}

/** Bind the lifecycle flushes. A tab is reaped without warning on iOS. */
function bindLifecycle(): void {
  const doc = globalThis.document;
  const win = globalThis.window;
  doc?.addEventListener('visibilitychange', () => {
    if (doc.visibilityState === 'hidden') post('hidden');
  });
  win?.addEventListener('pagehide', () => post('pagehide'));
  win?.addEventListener('freeze', () => post('freeze'));
}

/** Arm the stream. Everything above is inert until this runs. */
export function initTelemetry(opts: TelemetryOptions): void {
  options = opts;
  enabled = opts.enabled;
  seq = 0;
  sessionId = mintSessionId();
  buffer = [];
  bindLifecycle();
  if (enabled) void drain();
}

/** Disarm completely. Tests use this; the game never does. */
export function resetTelemetry(): void {
  options = null;
  enabled = false;
  buffer = [];
  seq = 0;
  sessionId = '';
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
}

/**
 * A monotonic stopwatch of *active* milliseconds.
 *
 * `performance.now()` because it cannot go backwards over a clock adjustment,
 * and paused while the tab is hidden because a session left open overnight is
 * not a two-hour session.
 */
export function createStopwatch(): () => number {
  const doc = globalThis.document;
  const clock = () => globalThis.performance?.now?.() ?? 0;
  let elapsed = 0;
  let since: number | null = clock();

  doc?.addEventListener('visibilitychange', () => {
    if (doc.visibilityState === 'hidden') {
      if (since !== null) elapsed += clock() - since;
      since = null;
    } else if (since === null) {
      since = clock();
    }
  });

  return () => elapsed + (since === null ? 0 : clock() - since);
}
